import { useEffect, useRef } from 'react';

import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import { useSelector } from 'react-redux';
import { logOperationalEvent } from 'saddlebag-logger';

import getFeatureDecision from '../../../../config';
import { COMBO_RECOMMENDATION_TYPE } from '../../../../config/constants';
import { COMPONENT_CAPYBARA_RECOMMENDATIONS } from '../../helpers/constants/analyticsConstants';
import { useComboRecommendation } from '../../helpers/hooks/useComboRecommendation';
import { useTranslation } from '../../helpers/hooks/useTranslation';
import { useHotelContent } from '../../network/hooks/hotel-content/useHotelContent';
import {
  getRecommendationsError,
  getMichelinHotelId,
  isComboRecommendationLoading,
} from '../../selectors/michelinSelectors';

import RecommendationCard from './RecommendationCard';
import RecommendationCardSkeleton from './RecommendationCardSkeleton';

import type { GetResultUrl } from '../../../../services/contextAdaptor/types';

import './RecommendedForYou.scss';

type RecommendedForYouProps = {
  getResultUrl: GetResultUrl;
};

const RecommendedForYou = ({ getResultUrl }: RecommendedForYouProps) => {
  const translate = useTranslation();
  const isLoading = useSelector(isComboRecommendationLoading);
  const hasError = useSelector(getRecommendationsError);
  const hotelId = useSelector(getMichelinHotelId);

  useHotelContent(hotelId);

  const { comboRecommendation } = useComboRecommendation(getResultUrl);

  const isCheapest =
    getFeatureDecision(COMBO_RECOMMENDATION_TYPE, 'cheapest') === 'cheapest';
  const headingKey = isCheapest
    ? 'ktxt_combo_rec_heading_cheap'
    : 'ktxt_combo_rec_heading';
  const explanationKey = isCheapest
    ? 'ktxt_combo_rec_explanation_cheap'
    : 'ktxt_combo_rec_explanation';

  const logState = useRef<'idle' | 'loading' | 'logged'>('idle');

  useEffect(() => {
    if (isLoading) {
      logState.current = 'loading';
      return;
    }

    if (logState.current === 'logged') return;
    if (logState.current === 'idle' && !hasError && !comboRecommendation)
      return;

    logOperationalEvent({
      component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
      eventName: 'rendered',
      result: comboRecommendation ? 'true' : 'false',
    });
    logState.current = 'logged';
  }, [isLoading, comboRecommendation, hasError]);

  if (hasError || (!comboRecommendation && !isLoading)) {
    return null;
  }

  return (
    <div
      className="recommended-for-you"
      data-testid="recommended-for-you"
      aria-live="polite"
    >
      <div className="recommended-for-you__header">
        <div className="recommended-for-you__title">
          <BpkText textStyle={TEXT_STYLES.heading3} tagName="h2">
            {translate(headingKey)}
          </BpkText>
        </div>
        <span className="recommended-for-you__explanation">
          <BpkText textStyle={TEXT_STYLES.footnote}>
            {translate(explanationKey)}
          </BpkText>
        </span>
      </div>

      {isLoading && <RecommendationCardSkeleton />}
      {!isLoading && comboRecommendation && (
        <RecommendationCard recommendation={comboRecommendation} />
      )}
    </div>
  );
};

export default RecommendedForYou;
